import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "backfill_user_campus", label: "Backfilling user→campus bindings" },
  { key: "finance", label: "Reseeding fee structures, invoices & expenses" },
  { key: "subject_attendance", label: "Reseeding subject-wise attendance (54K records)" },
  { key: "library", label: "Reseeding books, loans & overdues" },
];

export default function DemoResetDialog({ onClose }) {
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  // #28 UX: require the operator to type an exact string before the button
  // enables, on top of the checkbox. Belt + suspenders vs an accidental click.
  const [typedConfirm, setTypedConfirm] = useState("");
  const readyToRun = confirmed && typedConfirm === "DELETE";
  const [result, setResult] = useState(null);
  const qc = useQueryClient();

  const runReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        "/admin/demo-reset",
        { confirm: "DELETE_ALL_DEMO_DATA" },
        { timeout: 120000 },
      );
      setResult(data);
      if (data.ok) {
        toast.success(`Demo reset complete in ${(data.total_duration_ms / 1000).toFixed(1)}s`);
        qc.invalidateQueries(); // refresh every cached page
      } else {
        toast.error("Some steps failed — see details below");
      }
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || e.message;
      toast.error(`Reset failed: ${msg}`);
      setResult({ ok: false, steps: [{ step: "request", status: "error", error: msg }] });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="demo-reset-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Reset demo data
          </DialogTitle>
          <DialogDescription>
            Re-runs all seed scripts so finance, attendance and library numbers look fresh-as-of-today.
            Existing user accounts and uploaded branding are <strong>not</strong> touched.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <>
            <div className="border border-amber-300 bg-amber-50 text-amber-800 rounded-md p-3 text-xs flex gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                This will <strong>wipe and re-seed</strong> all fee invoices, expenses, subject-wise attendance
                sessions/records and library books/loans for the St. Peter&apos;s tenant. Typically takes 25–40s.
              </div>
            </div>

            <div className="space-y-1.5 mt-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Steps</div>
              {STEPS.map((s, i) => (
                <div key={s.key} className="flex items-center gap-2 text-xs">
                  <div className="h-5 w-5 rounded-full border border-border flex items-center justify-center text-[10px] font-mono">{i + 1}</div>
                  <span>{s.label}</span>
                </div>
              ))}
            </div>

            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                data-testid="demo-reset-confirm-checkbox"
              />
              I understand existing demo data will be wiped and replaced.
            </label>

            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">
                Type <span className="font-mono font-bold text-foreground">DELETE</span> to confirm:
              </label>
              <input
                type="text"
                value={typedConfirm}
                onChange={(e) => setTypedConfirm(e.target.value)}
                placeholder="Type DELETE"
                className="w-full h-9 px-3 rounded-md border bg-background text-sm font-mono focus:outline-none focus:ring-2 focus:ring-rose-500/40"
                data-testid="demo-reset-type-confirm"
                autoComplete="off"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button onClick={runReset} disabled={!readyToRun || busy} data-testid="demo-reset-run">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Running…</> : "Run reset"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className={`border rounded-md p-3 text-xs flex gap-2 items-start ${result.ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-rose-300 bg-rose-50 text-rose-800"}`}>
              {result.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 mt-0.5 shrink-0" />}
              <div>
                {result.ok ? "All steps completed successfully" : "Reset finished with errors"}
                {result.total_duration_ms != null && (
                  <span className="ml-2 opacity-70">· {(result.total_duration_ms / 1000).toFixed(1)}s</span>
                )}
              </div>
            </div>

            <div className="space-y-1.5 mt-2 max-h-60 overflow-y-auto" data-testid="demo-reset-results">
              {result.steps.map((s) => (
                <div key={s.step} className="flex items-start gap-2 text-xs" data-testid={`reset-step-${s.step}`}>
                  {s.status === "ok"
                    ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                    : <XCircle className="h-3.5 w-3.5 text-rose-600 mt-0.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{s.step}</div>
                    {s.error && <div className="text-rose-600 text-[10px] mt-0.5 break-words">{s.error}</div>}
                  </div>
                  {s.duration_ms != null && (
                    <div className="text-muted-foreground tabular-nums">{(s.duration_ms / 1000).toFixed(2)}s</div>
                  )}
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button onClick={onClose} data-testid="demo-reset-close">Close</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
